// Dunkle Seitenleiste: oben Marke und (im Rahmen) der Programmwechsel, darunter die Seiten des Programms mit
// Gruppen, unten Benutzer, Abmelden und Einklappen. Navigiert über `gehe` – der Rahmen lädt dann das passende
// Programm, ein allein laufendes Programm wechselt nur die Seite.
import { ChevronsUpDown, LogOut, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { useSichtbareSeiten } from "./seitenleisteLogik";
import { PROGRAMMNAME, VERSION } from "../kern/marke";
import { useSitzung } from "../kern/sitzung";
import { BEREICHE, PROGRAMME, type Programm, type ProgrammId, type Seite } from "../navigation/katalog";
import { Symbol } from "../navigation/Symbol";

function Verweis({ pfad, gehe, className, title, children, aktiv, role }: {
  pfad: string; gehe: (pfad: string) => void; className: string; title?: string; children: ReactNode; aktiv: boolean;
  role?: string;
}) {
  return (
    <a href={pfad} className={className} title={title} role={role} aria-current={aktiv ? "page" : undefined}
      onClick={(e: MouseEvent) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;  // neuer Tab bleibt möglich
        e.preventDefault();
        gehe(pfad);
      }}>
      {children}
    </a>
  );
}

function ProgrammWahl({ aktuell, programme, ziel, gehe, eingeklappt }: {
  aktuell: Programm | undefined; programme: Programm[]; ziel: (p: Programm) => string; gehe: (pfad: string) => void;
  eingeklappt: boolean;
}) {
  const [offen, setOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);
  // Die Seitenleiste scrollt (overflow) und schnitte ein breiteres Menü ab – darum liegt es fest über der Seite
  const [lage, setLage] = useState<{ top: number; left: number } | null>(null);
  const oeffnen = () => {
    const r = huelle.current?.getBoundingClientRect();
    if (r) setLage(eingeklappt ? { top: r.top, left: r.right + 10 } : { top: r.bottom + 4, left: r.left });
    setOffen(!offen);
  };
  useEffect(() => {
    if (!offen) return;
    const weg = (e: globalThis.MouseEvent) => { if (!huelle.current?.contains(e.target as Node)) setOffen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOffen(false); };
    document.addEventListener("mousedown", weg);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", weg); document.removeEventListener("keydown", esc); };
  }, [offen]);
  return (
    <div className="im-programmwahl" ref={huelle}>
      <button type="button" className="im-programmwahl__knopf" aria-label="Programm wechseln" aria-haspopup="menu"
        aria-expanded={offen} title={eingeklappt ? `Programm: ${aktuell?.titel ?? ""}` : undefined} onClick={oeffnen}>
        {aktuell && <Symbol name={aktuell.symbol} groesse={18} />}
        <span className="im-nav__text">{aktuell?.titel ?? "Programm"}</span>
        <ChevronsUpDown className="im-nav__text" size={16} strokeWidth={1.5} aria-hidden="true" />
      </button>
      {offen && (
        <div className="im-programmwahl__menue" role="menu" aria-label="Programme"
          style={lage ? { position: "fixed", top: lage.top, left: lage.left } : undefined}>
          {programme.map((p) => (
            <Verweis key={p.id} pfad={ziel(p)} role="menuitem" className="im-programmwahl__eintrag" aktiv={p.id === aktuell?.id}
              gehe={(z) => { setOffen(false); gehe(z); }}>
              <Symbol name={p.symbol} groesse={18} />
              <span><b>{p.titel}</b><small>{p.beschreibung}</small></span>
            </Verweis>
          ))}
        </div>
      )}
    </div>
  );
}

export function Seitenleiste({ programm, pfad, gehe, mitProgrammwahl, eingeklappt, setEingeklappt, abmelden }: {
  programm: ProgrammId; pfad: string; gehe: (pfad: string) => void; mitProgrammwahl: boolean;
  eingeklappt: boolean; setEingeklappt: (x: boolean) => void; abmelden: () => void;
}) {
  const { ich } = useSitzung();
  const { seitenVon, programme } = useSichtbareSeiten();
  const aktuell = PROGRAMME.find((p) => p.id === programm);
  const seiten = seitenVon(programm);
  const mehrereBereiche = new Set(seiten.map((s) => s.bereich)).size > 1;
  const gruppeVon = (s: Seite) => s.gruppe ?? (mehrereBereiche ? BEREICHE.find((b) => b.id === s.bereich)?.titel : undefined);
  const symbolVon = (s: Seite) => s.symbol ?? BEREICHE.find((b) => b.id === s.bereich)?.symbol ?? "bausteine";

  // Seiten einer Gruppe zusammenhalten (Reihenfolge der Gruppen wie im Katalog), sonst stünde eine Gruppe doppelt da,
  // sobald eine ihrer Seiten im Katalog später kommt (Tools: „Datei komprimieren“ hinter „Vergleichen & Erzeugen“)
  const jeGruppe = new Map<string | undefined, Seite[]>();
  for (const s of seiten) jeGruppe.set(gruppeVon(s), [...(jeGruppe.get(gruppeVon(s)) ?? []), s]);
  const eintraege: ReactNode[] = [];
  for (const [g, liste] of jeGruppe) {
    if (g) eintraege.push(<div key={`g-${g}`} className="im-nav__group">{g}</div>);
    for (const s of liste) eintraege.push(
      <Verweis key={s.pfad} pfad={s.pfad} gehe={gehe} aktiv={s.pfad === pfad}
        className={s.unter ? "im-nav__item im-nav__item--unter" : "im-nav__item"} title={eingeklappt ? s.titel : undefined}>
        <Symbol name={symbolVon(s)} groesse={18} />
        <span className="im-nav__text">{s.titel}</span>
        <span />
      </Verweis>,
    );
  }

  return (
    <nav className="im-nav" aria-label="Arbeitsbereiche">
      <div className="im-nav__brand" title={`${PROGRAMMNAME} ${VERSION}`}><i />
        <span className="im-nav__text">{PROGRAMMNAME} <span className="im-nav__version">{VERSION}</span></span>
      </div>
      {mitProgrammwahl ? (
        <ProgrammWahl aktuell={aktuell} programme={programme} eingeklappt={eingeklappt} gehe={gehe}
          ziel={(p) => seitenVon(p.id)[0]?.pfad ?? "/"} />
      ) : aktuell && (
        <div className="im-programmwahl__knopf im-programmwahl__knopf--fest" title={eingeklappt ? aktuell.titel : undefined}>
          <Symbol name={aktuell.symbol} groesse={18} /><span className="im-nav__text">{aktuell.titel}</span><span />
        </div>
      )}
      {eintraege}
      <div className="im-nav__fuss">
        <span className="im-nav__ich im-nav__text" title={ich?.name}>
          <span className="im-key">{ich?.kuerzel}</span> {ich?.name}
        </span>
        <button type="button" className="im-nav__item im-nav__abmelden" title={eingeklappt ? "Abmelden" : undefined} onClick={abmelden}>
          <LogOut size={18} strokeWidth={1.5} aria-hidden="true" /><span className="im-nav__text">Abmelden</span><span />
        </button>
        <button type="button" className="im-nav__item im-nav__klappen"
          aria-label={eingeklappt ? "Seitenleiste ausklappen" : "Seitenleiste einklappen"}
          title={`${eingeklappt ? "Ausklappen" : "Einklappen"} ( [ )`} onClick={() => setEingeklappt(!eingeklappt)}>
          {eingeklappt ? <PanelLeftOpen size={18} strokeWidth={1.5} aria-hidden="true" />
            : <PanelLeftClose size={18} strokeWidth={1.5} aria-hidden="true" />}
          <span className="im-nav__text">Einklappen</span><span className="im-nav__text im-kbd">[</span>
        </button>
      </div>
    </nav>
  );
}
